import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Container, Modal, Button, Form, Spinner, Alert } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaTshirt,
  FaShoppingBag,
  FaMagic,
  FaDownload,
  FaTimes,
  FaPlus,
  FaRegCopy,
  FaCheck,
  FaHistory,
  FaGem
} from "react-icons/fa";
import {
  GiSparkles,
  GiHighHeel,
  GiSocks,
  GiMirrorMirror,
  GiRibbon,
  GiBelt,
  GiDress,
  GiBilledCap,
  GiMonclerJacket,
  GiTrousers
} from "react-icons/gi";
import { IoShirtOutline, IoSparkles } from "react-icons/io5";
import "../css/Wafit.css";

// 기본 모델 이미지 (필수 항목 기본값)
const DEFAULT_MODEL_IMAGE = "https://www.wajok.com/attachments/image_1.png";

// 슬롯 정의 (실전 패션 이커머스 최적화 규격: 총 9개 코디 슬롯 + 1개 기본 모델)
const SLOT_CONFIGS = {
  // 인물 모델 (중앙 아치 거울)
  image_1: { key: "image_1", label: "피팅 모델", shortLabel: "모델", enLabel: "MODEL", shortEn: "MODEL", icon: <GiMirrorMirror />, category: "model", desc: "전신 모델 이미지 (필수)" },

  // 좌측 선반: 모자/헤드웨어 (image_9), 아우터 (image_2), 상의/원피스 (image_3), 포인트 악세사리 (image_10)
  image_9: { key: "image_9", label: "모자/헤드웨어", shortLabel: "모자", enLabel: "HEADWEAR", shortEn: "HEAD", icon: <GiBilledCap />, category: "acc", desc: "볼캡, 비니, 버킷햇, 두건, 후드 등" },
  image_2: { key: "image_2", label: "아우터 (Outerwear)", shortLabel: "아우터", enLabel: "OUTERWEAR", shortEn: "OUTER", icon: <GiMonclerJacket />, category: "clothes", desc: "자켓, 코트, 가디건, 패딩, 점퍼 등" },
  image_3: { key: "image_3", label: "상의/이너 (Tops)", shortLabel: "상의", enLabel: "TOPS", shortEn: "TOPS", icon: <FaTshirt />, category: "clothes", desc: "셔츠, 니트, 티셔츠 (원피스 모드 시 원피스/점프수트)" },
  image_10: { key: "image_10", label: "포인트 악세사리", shortLabel: "악세사리", enLabel: "ACCESSORIES", shortEn: "ACC", icon: <FaGem />, category: "acc", desc: "목걸이, 귀걸이, 선글라스, 머플러, 스카프 등" },

  // 우측 선반: 가방 (image_7), 벨트 (image_5), 하의 (image_4), 양말 (image_8), 신발 (image_6)
  image_7: { key: "image_7", label: "가방/핸드백", shortLabel: "가방", enLabel: "BAGS", shortEn: "BAG", icon: <FaShoppingBag />, category: "goods", desc: "핸드백, 토트백, 숄더백, 크로스백 등" },
  image_5: { key: "image_5", label: "벨트 (Belt)", shortLabel: "벨트", enLabel: "BELT", shortEn: "BELT", icon: <GiBelt />, category: "goods", desc: "가죽 벨트, 허리 벨트, 포인트 벨트 등" },
  image_4: { key: "image_4", label: "하의 (Bottoms)", shortLabel: "하의", enLabel: "BOTTOMS", shortEn: "BOTTOM", icon: <GiTrousers />, category: "clothes", desc: "팬츠, 슬랙스, 데님, 스커트, 쇼츠 등" },
  image_8: { key: "image_8", label: "양말/스타킹", shortLabel: "양말", enLabel: "HOSIERY", shortEn: "SOCKS", icon: <GiSocks />, category: "goods", desc: "삭스, 타이즈, 스타킹 등" },
  image_6: { key: "image_6", label: "신발/슈즈", shortLabel: "신발", enLabel: "FOOTWEAR", shortEn: "SHOES", icon: <GiHighHeel />, category: "goods", desc: "스니커즈, 로퍼, 플랫, 부츠, 샌들 등" }
};

// 좌측 / 우측 컬럼 배치용 슬롯 키 (좌측 4개, 우측 5개)
const LEFT_SLOT_KEYS = ["image_9", "image_2", "image_3", "image_10"];
const RIGHT_SLOT_KEYS = ["image_7", "image_5", "image_4", "image_8", "image_6"];

// 로딩 중 노출할 팁 메시지들
const LOADING_TIPS = [
  "✨ AI 스타일리스트가 의류와 악세사리를 정교하게 피팅하고 있어요!",
  "💄 화장대 조명을 맞추고 조화로운 핏을 완성하는 중이에요.",
  "👗 착용 부위가 많을수록 더 입체적인 피팅 결과가 탄생해요.",
  "🎀 완성된 코디는 좌측 마이 룩북에 보관되고 다운로드할 수 있어요.",
  "✨ 최대 2~5분 정도 정성스럽게 렌더링되니 잠시만 기다려주세요 💕"
];

export default function Wafit() {
  // 1. 착용 슬롯 상태 (image_1 은 기본 모델 이미지)
  const [slots, setSlots] = useState(() => {
    const saved = localStorage.getItem("wafit_equipped_slots");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return {
          image_1: parsed.image_1 || DEFAULT_MODEL_IMAGE,
          ...parsed
        };
      } catch (e) {
        console.error("Failed to parse saved slots", e);
      }
    }
    return {
      image_1: DEFAULT_MODEL_IMAGE,
      image_2: null,
      image_3: null,
      image_4: null,
      image_5: null,
      image_6: null,
      image_7: null,
      image_8: null,
      image_9: null,
      image_10: null
    };
  });

  // 2. 원피스 유무 (is_onepiece)
  const [isOnepiece, setIsOnepiece] = useState(() => {
    return localStorage.getItem("wafit_is_onepiece") === "true";
  });

  // 3. 하단 아이템 보관함 리스트
  const [storageItems, setStorageItems] = useState(() => {
    const saved = localStorage.getItem("wafit_storage_items");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error("Failed to parse saved storage", e);
      }
    }
    return [
      {
        id: "default-model-item",
        slotKey: "image_1",
        slotLabel: "기본 모델",
        category: "model",
        imageUrl: DEFAULT_MODEL_IMAGE,
        name: "기본 모델 (스튜디오 컷)",
        createdAt: new Date().toISOString()
      }
    ];
  });

  // 4. 하단 보관함 탭 선택 (all, clothes, acc, bag_shoes, model)
  const [activeStorageTab, setActiveStorageTab] = useState("all");

  // 5. 좌측 과거 생성 룩북 히스토리
  const [historyList, setHistoryList] = useState(() => {
    const saved = localStorage.getItem("wafit_lookbook_history");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error("Failed to parse saved history", e);
      }
    }
    return [];
  });

  // 6. 모바일 전용 뷰 탭 (fitting: 피팅룸/보관함 | lookbook: 마이 룩북/스탯)
  const [mobileTab, setMobileTab] = useState("fitting");

  // 7. 모달 관련 상태
  // (1) 슬롯 아이템 등록 모달
  const [showItemModal, setShowItemModal] = useState(false);
  const [activeSlotKey, setActiveSlotKey] = useState("image_3");
  const [itemInputTab, setItemInputTab] = useState("file"); // "file" | "url"
  const [inputUrl, setInputUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  // (2) 룩북 고화질 뷰어 모달
  const [showViewerModal, setShowViewerModal] = useState(false);
  const [selectedLook, setSelectedLook] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);

  // 7. 생성(합성) 진행 상태 & 타이머
  const [isGenerating, setIsGenerating] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [tipIndex, setTipIndex] = useState(0);
  const timerRef = useRef(null);
  const tipTimerRef = useRef(null);

  // 로컬스토리지 동기화
  useEffect(() => {
    localStorage.setItem("wafit_equipped_slots", JSON.stringify(slots));
  }, [slots]);

  useEffect(() => {
    localStorage.setItem("wafit_is_onepiece", String(isOnepiece));
  }, [isOnepiece]);

  useEffect(() => {
    localStorage.setItem("wafit_storage_items", JSON.stringify(storageItems));
  }, [storageItems]);

  useEffect(() => {
    localStorage.setItem("wafit_lookbook_history", JSON.stringify(historyList));
  }, [historyList]);

  // 생성 중 타이머 및 팁 롤링
  useEffect(() => {
    if (isGenerating) {
      setElapsedSeconds(0);
      timerRef.current = setInterval(() => {
        setElapsedSeconds((prev) => prev + 1);
      }, 1000);

      tipTimerRef.current = setInterval(() => {
        setTipIndex((prev) => (prev + 1) % LOADING_TIPS.length);
      }, 7000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
      if (tipTimerRef.current) clearInterval(tipTimerRef.current);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (tipTimerRef.current) clearInterval(tipTimerRef.current);
    };
  }, [isGenerating]);

  // 경과 시간 포맷 (MM:SS)
  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  // 착용 가능 최대 부위 수: 원피스 모드 시 8개 (하의 제외), 투피스 모드 시 9개
  const maxEquippable = isOnepiece ? 8 : 9;

  // 장착된 부위 개수 계산 (image_1 제외 부위, 원피스 모드 시 image_4 제외)
  const equippedCount = Object.keys(slots).filter(
    (key) => key !== "image_1" && (!isOnepiece || key !== "image_4") && slots[key]
  ).length;

  // 러블리 스타일 지수 계산 (게이미피케이션)
  const lovelyScore = Math.min(100, Math.round(20 + (equippedCount / maxEquippable) * 80));

  // =========================================================================
  // 핸들러: 원피스 모드 토글 (활성화 시 하의 자동 탈의)
  // =========================================================================
  const handleToggleOnepiece = (e) => {
    e?.stopPropagation();
    setIsOnepiece((prev) => {
      const next = !prev;
      if (next) {
        // 원피스 활성화 시 하의(image_4) 자동 장착 해제
        setSlots((s) => ({ ...s, image_4: null }));
      }
      return next;
    });
  };

  // =========================================================================
  // 핸들러: 슬롯 클릭 -> 모달 오픈
  // =========================================================================
  const handleOpenSlotModal = (slotKey) => {
    if (isOnepiece && slotKey === "image_4") {
      alert("원피스 모드가 활성화되어 있어 하의를 등록할 수 없습니다.\n상의 슬롯의 원피스 토글을 OFF로 전환 후 등록해주세요 💕");
      return;
    }
    setActiveSlotKey(slotKey);
    setInputUrl("");
    setUploadError("");
    setItemInputTab("file");
    setShowItemModal(true);
  };

  // 슬롯 아이템 해제/삭제
  const handleRemoveSlot = (e, slotKey) => {
    e.stopPropagation();
    if (slotKey === "image_1") {
      // 모델은 기본 모델로 복구
      setSlots((prev) => ({ ...prev, image_1: DEFAULT_MODEL_IMAGE }));
    } else {
      setSlots((prev) => ({ ...prev, [slotKey]: null }));
    }
  };

  // =========================================================================
  // 이미지 업로드 처리: n8n webhook/api/attachments 호출
  // =========================================================================
  const uploadImageFile = async (file, customName) => {
    const formData = new FormData();
    formData.append("attachment", file);

    const response = await axios.post(
      "https://n8n.wajok.com/webhook/api/attachments",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data"
        },
        timeout: 120000 // 2분
      }
    );

    if (
      response.data &&
      (response.data.status === "0000" || response.data.data?.fileName)
    ) {
      const uploadedUrl = response.data.data.fileName;
      const originalFileName =
        response.data.data.originalFileName || customName || file.name || "아이템";

      // 1. 현재 선택된 슬롯에 즉시 장착
      setSlots((prev) => ({ ...prev, [activeSlotKey]: uploadedUrl }));

      const isDressItem = activeSlotKey === "image_3" && isOnepiece;
      const currentSlotLabel = isDressItem ? "원피스" : (SLOT_CONFIGS[activeSlotKey]?.label || "아이템");

      // 2. 하단 보관함에 새 아이템 추가
      const newItem = {
        id: "item-" + Date.now(),
        slotKey: activeSlotKey,
        slotLabel: currentSlotLabel,
        category: SLOT_CONFIGS[activeSlotKey]?.category || "clothes",
        imageUrl: uploadedUrl,
        name: originalFileName,
        createdAt: new Date().toISOString()
      };

      setStorageItems((prev) => [newItem, ...prev]);
      setShowItemModal(false);
      return uploadedUrl;
    } else {
      throw new Error(response.data?.message || "파일 업로드에 실패했습니다.");
    }
  };

  // 파일 입력 체인지
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      setUploadError("");
      await uploadImageFile(file, file.name);
    } catch (err) {
      console.error("Upload error:", err);
      setUploadError(err.message || "파일 업로드 중 오류가 발생했습니다.");
    } finally {
      setUploading(false);
    }
  };

  // URL 입력 처리 (외부 URL도 attachments API로 변환)
  const handleUrlSubmit = async (e) => {
    e.preventDefault();
    if (!inputUrl.trim()) {
      setUploadError("이미지 URL을 입력해주세요.");
      return;
    }

    const trimmedUrl = inputUrl.trim();
    const isDressItem = activeSlotKey === "image_3" && isOnepiece;
    const currentSlotLabel = isDressItem ? "원피스" : (SLOT_CONFIGS[activeSlotKey]?.label || "아이템");

    // 이미 wajok 첨부파일 URL인 경우 바로 장착
    if (trimmedUrl.startsWith("https://www.wajok.com/attachments/")) {
      setSlots((prev) => ({ ...prev, [activeSlotKey]: trimmedUrl }));
      const newItem = {
        id: "item-" + Date.now(),
        slotKey: activeSlotKey,
        slotLabel: currentSlotLabel,
        category: SLOT_CONFIGS[activeSlotKey]?.category || "clothes",
        imageUrl: trimmedUrl,
        name: `${currentSlotLabel} (URL)`,
        createdAt: new Date().toISOString()
      };
      setStorageItems((prev) => [newItem, ...prev]);
      setShowItemModal(false);
      return;
    }

    // 외부 URL일 경우: 백엔드 프록시 API 경유 -> Blob 변환 후 n8n attachments 업로드
    setUploading(true);
    setUploadError("");

    try {
      let blob;

      // 1. 1차 시도: 자체 Express 백엔드 프록시 API 경유 (/api/proxy-image)
      try {
        const proxyUrl = `/api/proxy-image?url=${encodeURIComponent(trimmedUrl)}`;
        const res = await fetch(proxyUrl);
        if (res.ok) {
          blob = await res.blob();
        }
      } catch (proxyErr) {
        console.warn("Backend proxy fetch failed, trying fallback:", proxyErr);
      }

      // 2. 2차 시도: 브라우저 직접 fetch (CORS 허용 사이트 대응)
      if (!blob || blob.size === 0) {
        try {
          const directRes = await fetch(trimmedUrl, { mode: "cors" });
          if (directRes.ok) {
            blob = await directRes.blob();
          }
        } catch (directErr) {
          console.warn("Direct fetch failed:", directErr);
        }
      }

      // 3. 3차 시도: 공용 CORS 프록시 fallback
      if (!blob || blob.size === 0) {
        try {
          const publicProxyRes = await fetch(`https://corsproxy.io/?${encodeURIComponent(trimmedUrl)}`);
          if (publicProxyRes.ok) {
            blob = await publicProxyRes.blob();
          }
        } catch (pubErr) {
          console.warn("Public proxy fetch failed:", pubErr);
        }
      }

      if (!blob || blob.size === 0) {
        throw new Error("외부 사이트에서 이미지를 가져올 수 없습니다.");
      }

      // Blob MIME 타입에 맞는 확장자 추출
      const mimeType = blob.type || "image/png";
      let ext = mimeType.split("/")[1] || "png";
      if (ext.includes(";")) ext = ext.split(";")[0];
      if (ext.includes("+")) ext = ext.split("+")[0];
      const file = new File([blob], `web_image_${Date.now()}.${ext}`, { type: mimeType });

      await uploadImageFile(file, "웹 등록 아이템");
    } catch (err) {
      console.warn("Image fetch and upload failed:", err);
      setUploadError(
        "💡 외부 사이트 보안으로 인해 링크 직접 수집이 제한되었습니다. 해당 이미지를 컴퓨터/휴대폰에 저장한 후 [파일 직접 업로드] 탭에서 등록해주세요!"
      );
    } finally {
      setUploading(false);
    }
  };

  // =========================================================================
  // 하단 보관함 아이템 클릭 -> 상단 해당 슬롯 원클릭 장착
  // =========================================================================
  const handleEquipFromStorage = (item) => {
    if (isOnepiece && item.slotKey === "image_4") {
      alert("원피스 모드가 활성화되어 있어 하의를 장착할 수 없습니다.\n상의 슬롯의 원피스 토글을 OFF로 전환 후 착용해주세요 💕");
      return;
    }
    setSlots((prev) => ({
      ...prev,
      [item.slotKey]: item.imageUrl
    }));
  };

  // 보관함 아이템 삭제 (장착 중인 아이템일 경우 상단 슬롯에서도 자동 장착 해제)
  const handleDeleteStorageItem = (e, itemId) => {
    e.stopPropagation();
    if (window.confirm("보관함에서 이 아이템을 삭제할까요?")) {
      const targetItem = storageItems.find((item) => item.id === itemId);

      // 1. 해당 아이템이 현재 상단 슬롯에 장착되어 있다면 자동 장착 해제
      if (targetItem && targetItem.slotKey && slots[targetItem.slotKey] === targetItem.imageUrl) {
        setSlots((prev) => {
          if (targetItem.slotKey === "image_1") {
            // 모델인 경우 기본 모델로 복구
            return { ...prev, image_1: DEFAULT_MODEL_IMAGE };
          } else {
            // 의류 및 악세사리 슬롯 해제
            return { ...prev, [targetItem.slotKey]: null };
          }
        });
      }

      // 2. 보관함 리스트에서 제거
      setStorageItems((prev) => prev.filter((item) => item.id !== itemId));
    }
  };

  // 보관함 필터링 (전체, 의류, 잡화, 악세사리, 모델)
  const filteredStorageItems = storageItems.filter((item) => {
    if (activeStorageTab === "all") return true;
    if (activeStorageTab === "clothes") return item.category === "clothes";
    if (activeStorageTab === "goods") return item.category === "goods" || item.category === "bag_shoes";
    if (activeStorageTab === "acc") return item.category === "acc";
    if (activeStorageTab === "model") return item.category === "model";
    return true;
  });

  // =========================================================================
  // AI 피팅 생성 (POST https://n8n.wajok.com/webhook/api/wafit)
  // =========================================================================
  const handleGenerateFit = async () => {
    // 필수 항목: image_1 (모델)
    const modelUrl = slots.image_1 || DEFAULT_MODEL_IMAGE;

    // 장착된 부위가 최소 1개 이상인지 확인
    if (equippedCount === 0) {
      if (!window.confirm("장착된 코디 아이템이 없습니다. 모델 원본으로 생성을 진행할까요?")) {
        return;
      }
    }

    // 전송할 페이로드 구성 (빈 슬롯 제외, 원피스 모드 시 image_4 하의 제외)
    const payload = {
      is_onepiece: Boolean(isOnepiece),
      image_1: modelUrl
    };

    const codiSlotKeys = [
      "image_2",
      "image_3",
      "image_4",
      "image_5",
      "image_6",
      "image_7",
      "image_8",
      "image_9",
      "image_10"
    ];

    codiSlotKeys.forEach((key) => {
      if (isOnepiece && key === "image_4") return; // 원피스 모드 시 하의 전송 제외
      if (slots[key]) {
        payload[key] = slots[key];
      }
    });

    setIsGenerating(true);

    try {
      // 10분 이상 타임아웃 대응 (15분 = 900,000ms)
      const response = await axios.post(
        "https://n8n.wajok.com/webhook/api/wafit",
        payload,
        {
          headers: {
            "Content-Type": "application/json"
          },
          timeout: 900000
        }
      );

      const resData = response.data;
      const finalImageUrl =
        resData.image_url ||
        resData.storage_response?.data?.fileName ||
        resData.data?.fileName;

      if (finalImageUrl) {
        // 새 히스토리 룩북 아이템 생성
        const newLook = {
          id: "look-" + Date.now(),
          imageUrl: finalImageUrl,
          promptId: resData.prompt_id || "wafit-" + Date.now(),
          date: new Date().toLocaleDateString("ko-KR", {
            month: "long",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit"
          }),
          equippedCount: equippedCount,
          isOnepiece: isOnepiece
        };

        setHistoryList((prev) => [newLook, ...prev]);

        // 결과 자동 확대 보기
        setSelectedLook(newLook);
        setShowViewerModal(true);
      } else {
        alert("생성은 완료되었으나 이미지 URL을 수신하지 못했습니다.");
      }
    } catch (err) {
      console.error("Wafit generation error:", err);
      alert(
        "AI 피팅 생성 중 오류가 발생했습니다.\n" +
          (err.response?.data?.message || err.message || "잠시 후 다시 시도해주세요.")
      );
    } finally {
      setIsGenerating(false);
    }
  };

  // 룩북 이미지 다운로드
  const handleDownloadLook = async (imageUrl) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `wafit_fitting_${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      // 직접 링크 열기 fallback
      window.open(imageUrl, "_blank");
    }
  };

  // 이미지 링크 복사
  const handleCopyUrl = (url) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  // 룩북 이미지 삭제
  const handleDeleteLook = (e, lookId) => {
    e.stopPropagation();
    if (window.confirm("이 피팅 룩을 마이 룩북에서 삭제할까요?")) {
      setHistoryList((prev) => prev.filter((item) => item.id !== lookId));
      if (selectedLook && selectedLook.id === lookId) {
        setShowViewerModal(false);
        setSelectedLook(null);
      }
    }
  };

  return (
    <div className="wafit-container">
      {/* 반짝임 배경 효과 */}
      <div className="wafit-bg-sparkles" />

      <Container fluid="lg" className="position-relative">
        {/* 상단 헤더 카드 */}
        {/* 상단 헤더 카드 (모던 시크 부티크 아틀리에) */}
        <div className="wafit-header-card">
          <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
            <div>
              <div className="wafit-header-badge">GLAMOUR COUTURE • AI DRESSING ROOM</div>
              <h1 className="wafit-title">
                와핏 아틀리에 <span className="wafit-title-sub">WAFIT ATELIER</span>
              </h1>
              <p className="wafit-subtitle">
                선반 위의 감각적인 의류와 악세사리를 선택하여, 아치 미러 속 모델에게 프라이빗 피팅을 완성해보세요 ✨
              </p>
            </div>

            {/* 헤더 우측 컨트롤 (와핏! 생성 버튼) */}
            <div className="wafit-header-controls">
              <button
                type="button"
                className="wafit-generate-btn"
                onClick={handleGenerateFit}
                disabled={isGenerating}
              >
                <FaMagic /> <span>TRY ON • 와핏 피팅</span>
              </button>
            </div>
          </div>
        </div>

        {/* 모바일 전용 탭 세그먼트 (데스크탑에서는 d-none) */}
        <div className="wafit-mobile-tab-nav">
          <button
            type="button"
            className={`wafit-mobile-tab-btn ${mobileTab === "fitting" ? "active" : ""}`}
            onClick={() => setMobileTab("fitting")}
          >
            <GiMirrorMirror /> <span>피팅룸 & 쇼케이스</span>
          </button>
          <button
            type="button"
            className={`wafit-mobile-tab-btn ${mobileTab === "lookbook" ? "active" : ""}`}
            onClick={() => setMobileTab("lookbook")}
          >
            <GiSparkles /> <span>마이 룩북 ({historyList.length})</span>
          </button>
        </div>

        {/* 메인 2단/3단 레이아웃 (좌측 OOTD 룩북 & 스탯 / 우측 인벤토리 피팅룸) */}
        <div className={`wafit-main-grid mobile-tab-${mobileTab}`}>
          {/* ================================================================= */}
          {/* [좌측 영역] OOTD 스타일 룸 & 과거 생성 룩북 (디아블로4 스탯/재료창) */}
          {/* ================================================================= */}
          <div className="wafit-sidebar-card">
            {/* 게이미피케이션 스탯 박스 */}
            <div>
              <div className="wafit-section-title mb-2">
                <span>🎀 스타일링 스탯</span>
                <span className="badge-cnt">{equippedCount} / {maxEquippable} 부위</span>
              </div>
              <div className="wafit-stats-box">
                <div className="wafit-stat-row">
                  <span className="wafit-stat-label">
                    <IoSparkles style={{ color: "#f43f6e" }} /> 러블리 지수
                  </span>
                  <span className="wafit-stat-val">{lovelyScore} %</span>
                </div>
                <div className="wafit-stat-row">
                  <span className="wafit-stat-label">
                    <IoShirtOutline style={{ color: "#a855f7" }} /> 코디 모드
                  </span>
                  <span className="wafit-stat-val" style={{ color: "#7e22ce" }}>
                    {isOnepiece ? "원피스 룩 👗" : "투피스 룩 👚"}
                  </span>
                </div>
                <div className="wafit-stat-row">
                  <span className="wafit-stat-label">
                    <FaHistory style={{ color: "#3b82f6" }} /> 룩북 피팅 횟수
                  </span>
                  <span className="wafit-stat-val" style={{ color: "#2563eb" }}>
                    {historyList.length} 회
                  </span>
                </div>
              </div>
            </div>

            {/* 과거 피팅 룩북 히스토리 리스트 */}
            <div className="flex-grow-1 d-flex flex-column w-100" style={{ minHeight: 0 }}>
              <div className="wafit-section-title mb-2">
                <span>👗 마이 룩북</span>
                <span className="badge-cnt">{historyList.length}건</span>
              </div>

              <div className="wafit-history-list">
                {historyList.length === 0 ? (
                  <div className="wafit-history-empty">
                    <GiSparkles style={{ fontSize: "2rem", color: "#fda4b4", marginBottom: "8px" }} />
                    <br />
                    아직 생성된 피팅 룩이 없어요.
                    <br />
                    상단 <strong>와핏!</strong> 버튼을 눌러보세요!
                  </div>
                ) : (
                  historyList.map((look) => (
                    <motion.div
                      key={look.id}
                      className="wafit-history-item"
                      whileHover={{ scale: 1.02 }}
                      onClick={() => {
                        setSelectedLook(look);
                        setShowViewerModal(true);
                      }}
                    >
                      <img src={look.imageUrl} alt="생성 룩" loading="lazy" />
                      <button
                        type="button"
                        className="wafit-history-item-del-btn"
                        onClick={(e) => handleDeleteLook(e, look.id)}
                        title="룩북에서 삭제"
                      >
                        <FaTimes />
                      </button>
                      <div className="wafit-history-item-overlay">
                        <span>자세히 보기 🔍</span>
                        {look.date && (
                          <span className="wafit-history-item-date">{look.date}</span>
                        )}
                      </div>
                    </motion.div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* ================================================================= */}
          {/* ================================================================= */}
          {/* [중앙 피팅룸] 아치형 LED 미러 & 양옆 플로팅 선반 쇼룸 */}
          {/* ================================================================= */}
          <div className="wafit-dressing-room-card">
            <div className="wafit-stage-header mb-3">
              <div>
                <div className="wafit-stage-eyebrow">SHOWROOM MIRROR & FLOATING SHELVES</div>
                <h2 className="wafit-section-title mb-0">
                  <span>🪞 부티크 아치 미러 & 플로팅 선반</span>
                </h2>
              </div>
              <small className="wafit-stage-hint">
                각 선반의 슬롯을 터치하여 코디 아이템을 등록해보세요
              </small>
            </div>

            <div className="wafit-inventory-stage">
              {/* 좌측 플로팅 선반 컬럼 (모자, 아우터, 상의/원피스, 악세사리) */}
              <div className="wafit-slots-col">
                {LEFT_SLOT_KEYS.map((key) => {
                  const cfg = SLOT_CONFIGS[key];
                  const isTopOrDress = key === "image_3";
                  const isEquipped = Boolean(slots[key]);
                  const displayLabel = isTopOrDress && isOnepiece ? "원피스" : (cfg.shortLabel || cfg.label);
                  const displayEnLabel = isTopOrDress && isOnepiece ? "DRESS" : (cfg.shortEn || cfg.enLabel);
                  const displayIcon = isTopOrDress && isOnepiece ? <GiDress /> : cfg.icon;

                  return (
                    <div key={key} className="wafit-shelf-slot-wrapper">
                      <div className="wafit-shelf-tag">{displayEnLabel}</div>
                      <div
                        className={`wafit-slot-box ${isEquipped ? "is-equipped" : ""}`}
                        onClick={() => handleOpenSlotModal(key)}
                        title={`${displayLabel} 등록`}
                      >
                        {isEquipped ? (
                          <>
                            <img src={slots[key]} alt={displayLabel} className="wafit-slot-img" />
                            <button
                              type="button"
                              className="wafit-slot-delete-btn"
                              onClick={(e) => handleRemoveSlot(e, key)}
                              title="장착 해제"
                            >
                              <FaTimes />
                            </button>
                          </>
                        ) : (
                          <>
                            <div className="wafit-slot-icon">{displayIcon}</div>
                            <span className="wafit-slot-label">{displayLabel}</span>
                          </>
                        )}
                      </div>
                      <div className="wafit-floating-shelf-bar" />

                      {/* 상의/원피스 슬롯 하단에 원피스 코디 미니 토글 배치 */}
                      {isTopOrDress && (
                        <div
                          className={`wafit-mini-onepiece-toggle ${isOnepiece ? "active" : ""}`}
                          onClick={handleToggleOnepiece}
                          title={isOnepiece ? "원피스 모드 ON (하의 미착용)" : "투피스 모드 (상의+하의 코디)"}
                        >
                          {isOnepiece ? (
                            <GiDress style={{ color: "#b8826e", fontSize: "0.95rem" }} />
                          ) : (
                            <GiRibbon style={{ color: "#8c7379", fontSize: "0.85rem" }} />
                          )}
                          <span>
                            원피스 {isOnepiece ? "ON" : "OFF"}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* 중앙 모델 전신 아치 미러 스테이지 */}
              <div className="wafit-model-center-stage">
                <div className="wafit-arch-mirror-frame">
                  {/* 앰비언트 LED 후광 조명 */}
                  <div className="wafit-arch-led-halo" />
                  
                  <div
                    className="wafit-arch-mirror-glass"
                    onClick={() => handleOpenSlotModal("image_1")}
                    title="모델 사진 변경하기"
                  >
                    {/* 사용자 가변 이미지 비율 대응: 배경 앰비언트 블러 레이어 */}
                    <img
                      src={slots.image_1 || DEFAULT_MODEL_IMAGE}
                      alt="앰비언트 조명"
                      className="wafit-mirror-ambient-bg"
                    />
                    {/* 전면 피팅 모델 (contain으로 머리/발끝 잘림 없이 100% 보존) */}
                    <img
                      src={slots.image_1 || DEFAULT_MODEL_IMAGE}
                      alt="피팅 모델"
                      className="wafit-model-img"
                    />
                    <div className="wafit-model-change-btn">
                      <GiMirrorMirror /> 모델 사진 교체
                    </div>
                  </div>
                </div>
              </div>

              {/* 우측 플로팅 선반 컬럼 (가방, 벨트, 하의, 양말, 신발) */}
              <div className="wafit-slots-col">
                {RIGHT_SLOT_KEYS.map((key) => {
                  const cfg = SLOT_CONFIGS[key];
                  const isDisabled = isOnepiece && key === "image_4";
                  const isEquipped = !isDisabled && Boolean(slots[key]);
                  const displayLabel = isDisabled ? "하의 (OFF)" : (cfg.shortLabel || cfg.label);
                  const displayEnLabel = isDisabled ? "OFF" : (cfg.shortEn || cfg.enLabel);

                  return (
                    <div key={key} className={`wafit-shelf-slot-wrapper ${isDisabled ? "is-disabled" : ""}`}>
                      <div className={`wafit-shelf-tag ${isDisabled ? "opacity-50" : ""}`}>{displayEnLabel}</div>
                      <div
                        className={`wafit-slot-box ${isEquipped ? "is-equipped" : ""} ${isDisabled ? "is-disabled" : ""}`}
                        onClick={
                          isDisabled
                            ? () => alert("원피스 모드가 활성화되어 있어 하의를 착용할 수 없습니다 💕")
                            : () => handleOpenSlotModal(key)
                        }
                        title={isDisabled ? "원피스 모드 적용 중 (하의 비활성화)" : `${displayLabel} 등록`}
                      >
                        {isDisabled ? (
                          <div className="wafit-slot-disabled-content">
                            <div className="wafit-slot-icon opacity-50">{cfg.icon}</div>
                            <div className="wafit-slot-disabled-badge">원피스 모드</div>
                          </div>
                        ) : isEquipped ? (
                          <>
                            <img src={slots[key]} alt={displayLabel} className="wafit-slot-img" />
                            <button
                              type="button"
                              className="wafit-slot-delete-btn"
                              onClick={(e) => handleRemoveSlot(e, key)}
                              title="장착 해제"
                            >
                              <FaTimes />
                            </button>
                          </>
                        ) : (
                          <>
                            <div className="wafit-slot-icon">{cfg.icon}</div>
                            <span className="wafit-slot-label">{displayLabel}</span>
                          </>
                        )}
                      </div>
                      <div className="wafit-floating-shelf-bar" />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* =================================================================== */}
        {/* [하단 영역] 마이 드레스룸 벨벳 쇼케이스 (아이템 보관함 창) */}
        {/* =================================================================== */}
        <div className={`wafit-storage-card mobile-storage-${mobileTab}`}>
          <div className="wafit-storage-header">
            <div>
              <div className="wafit-stage-eyebrow">BOUTIQUE JEWELRY & FASHION TRAY</div>
              <h2 className="wafit-section-title mb-1">
                <span>🧰 마이 드레스룸 쇼케이스 서랍</span>
              </h2>
              <p className="mb-0 text-muted small">
                쇼케이스 트레이의 아이템을 터치하면 아치 미러에 즉시 장착됩니다.
              </p>
            </div>

            {/* 카테고리 탭 (전체, 의류, 잡화, 악세사리, 모델) */}
            <div className="wafit-tabs-wrapper">
              <button
                type="button"
                className={`wafit-tab-btn ${activeStorageTab === "all" ? "active" : ""}`}
                onClick={() => setActiveStorageTab("all")}
              >
                전체 ({storageItems.length})
              </button>
              <button
                type="button"
                className={`wafit-tab-btn ${activeStorageTab === "clothes" ? "active" : ""}`}
                onClick={() => setActiveStorageTab("clothes")}
              >
                👗 의류
              </button>
              <button
                type="button"
                className={`wafit-tab-btn ${activeStorageTab === "goods" ? "active" : ""}`}
                onClick={() => setActiveStorageTab("goods")}
              >
                👜 잡화
              </button>
              <button
                type="button"
                className={`wafit-tab-btn ${activeStorageTab === "acc" ? "active" : ""}`}
                onClick={() => setActiveStorageTab("acc")}
              >
                💍 악세사리
              </button>
              <button
                type="button"
                className={`wafit-tab-btn ${activeStorageTab === "model" ? "active" : ""}`}
                onClick={() => setActiveStorageTab("model")}
              >
                👤 모델
              </button>
            </div>
          </div>

          {/* 보관함 타일 그리드 */}
          <div className="wafit-storage-grid">
            {filteredStorageItems.length === 0 ? (
              <div className="wafit-storage-empty">
                보관함에 등록된 아이템이 없습니다.
                <br />
                상단 슬롯을 눌러 예쁜 옷이나 악세사리를 업로드해보세요!
              </div>
            ) : (
              filteredStorageItems.map((item) => {
                const isEquippedNow = slots[item.slotKey] === item.imageUrl;
                return (
                  <motion.div
                    key={item.id}
                    className={`wafit-storage-item ${isEquippedNow ? "is-currently-equipped" : ""}`}
                    whileHover={{ scale: 1.05 }}
                    onClick={() => handleEquipFromStorage(item)}
                    title={`${item.name} 장착하기`}
                  >
                    <img src={item.imageUrl} alt={item.name} loading="lazy" />
                    <div className="wafit-storage-item-label">{item.slotLabel}</div>
                    {item.id !== "default-model-item" && (
                      <button
                        type="button"
                        className="wafit-storage-item-del"
                        onClick={(e) => handleDeleteStorageItem(e, item.id)}
                        title="보관함에서 삭제"
                      >
                        ✕
                      </button>
                    )}
                  </motion.div>
                );
              })
            )}
          </div>
        </div>

        {/* 모바일 전용 하단 플로팅 피팅 액션 바 (피팅룸 탭일 때만 노출) */}
        {mobileTab === "fitting" && (
          <div className="wafit-mobile-fab-bar">
            <div className="wafit-mobile-fab-info">
              <span className="wafit-mobile-fab-badge">
                <IoSparkles style={{ color: "#f43f6e" }} /> {equippedCount}/{maxEquippable} 착용
              </span>
              <span className="wafit-mobile-fab-score">
                러블리 {lovelyScore}%
              </span>
            </div>
            <button
              type="button"
              className="wafit-mobile-fab-btn"
              onClick={handleGenerateFit}
              disabled={isGenerating}
            >
              <FaMagic /> <span>와핏 피팅</span>
            </button>
          </div>
        )}
      </Container>

      {/* =================================================================== */}
      {/* [모달 1] 아이템 등록 / 업로드 모달 */}
      {/* =================================================================== */}
      <Modal
        show={showItemModal}
        onHide={() => !uploading && setShowItemModal(false)}
        centered
        className="wafit-modal"
      >
        <Modal.Header closeButton={!uploading} className="wafit-modal-header">
          <Modal.Title className="wafit-modal-title">
            <span style={{ color: "#f43f6e" }}>
              {activeSlotKey === "image_3" && isOnepiece ? <GiDress /> : SLOT_CONFIGS[activeSlotKey]?.icon}
            </span>
            <span>
              {activeSlotKey === "image_3" && isOnepiece ? "원피스 (Dress)" : SLOT_CONFIGS[activeSlotKey]?.label} 등록
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="wafit-modal-body">
          <p className="text-muted small mb-3">
            {activeSlotKey === "image_3" && isOnepiece
              ? "원피스, 드레스, 점프수트 등 전신 의류 이미지"
              : SLOT_CONFIGS[activeSlotKey]?.desc}
            <br />
            컴퓨터나 스마트폰의 사진을 올리거나 이미지 링크(URL)를 입력해주세요.
          </p>

          {/* 탭 전환 (파일 업로드 vs URL 입력) */}
          <div className="wafit-tabs-wrapper mb-3">
            <button
              type="button"
              className={`wafit-tab-btn flex-grow-1 justify-content-center ${
                itemInputTab === "file" ? "active" : ""
              }`}
              onClick={() => setItemInputTab("file")}
              disabled={uploading}
            >
              📁 파일 직접 업로드
            </button>
            <button
              type="button"
              className={`wafit-tab-btn flex-grow-1 justify-content-center ${
                itemInputTab === "url" ? "active" : ""
              }`}
              onClick={() => setItemInputTab("url")}
              disabled={uploading}
            >
              🔗 이미지 URL 링크
            </button>
          </div>

          {uploadError && (
            <Alert variant="danger" className="py-2 small rounded-3">
              {uploadError}
            </Alert>
          )}

          {itemInputTab === "file" ? (
            <div>
              <label htmlFor="wafit-file-input" className="w-100" style={{ cursor: "pointer" }}>
                <div className="wafit-dropzone">
                  <div className="wafit-dropzone-icon">
                    {uploading ? <Spinner animation="border" variant="danger" /> : <FaPlus />}
                  </div>
                  <div className="wafit-dropzone-txt">
                    {uploading ? "서버에 안전하게 업로드 중..." : "이미지 파일 선택 또는 끌어놓기"}
                  </div>
                  <div className="wafit-dropzone-sub">JPG, PNG, WEBP 등 지원</div>
                </div>
              </label>
              <input
                id="wafit-file-input"
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleFileChange}
                disabled={uploading}
              />
            </div>
          ) : (
            <Form onSubmit={handleUrlSubmit}>
              <Form.Group className="mb-3">
                <Form.Label className="small fw-bold text-secondary">
                  웹 이미지 주소 (URL)
                </Form.Label>
                <Form.Control
                  type="url"
                  placeholder="https://example.com/image.jpg"
                  value={inputUrl}
                  onChange={(e) => setInputUrl(e.target.value)}
                  disabled={uploading}
                  className="rounded-3"
                />
              </Form.Group>
              <Button
                type="submit"
                variant="danger"
                className="w-100 rounded-3 py-2 fw-bold"
                disabled={uploading || !inputUrl.trim()}
                style={{ background: "linear-gradient(135deg, #f43f6e, #db2777)", border: "none" }}
              >
                {uploading ? (
                  <>
                    <Spinner size="sm" animation="border" className="me-2" />
                    가져오는 중...
                  </>
                ) : (
                  "아이템 등록 & 장착하기"
                )}
              </Button>
            </Form>
          )}
        </Modal.Body>
      </Modal>

      {/* =================================================================== */}
      {/* [모달 2] 룩북 고화질 뷰어 & 다운로드 모달 */}
      {/* =================================================================== */}
      <Modal
        show={showViewerModal}
        onHide={() => setShowViewerModal(false)}
        centered
        size="xl"
        dialogClassName="wafit-viewer-dialog"
        className="wafit-modal"
      >
        <Modal.Header closeButton className="wafit-modal-header">
          <Modal.Title className="wafit-modal-title">
            <GiSparkles style={{ color: "#b8826e" }} />
            <span>나만의 피팅 완성 룩북</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="wafit-modal-body p-3 p-md-4 text-center">
          {selectedLook && (
            <div>
              <div className="wafit-viewer-image-wrapper">
                <img
                  src={selectedLook.imageUrl}
                  alt="완성 룩"
                  className="wafit-viewer-image"
                />
              </div>

              <div className="mt-3 text-muted small">
                생성 일시: {selectedLook.date}
              </div>

              <div className="d-flex justify-content-center gap-2 mt-3 flex-wrap">
                <Button
                  variant="danger"
                  className="rounded-pill px-4 fw-bold shadow-sm"
                  style={{ background: "linear-gradient(135deg, #d4a38d, #b8826e)", border: "none" }}
                  onClick={() => handleDownloadLook(selectedLook.imageUrl)}
                >
                  <FaDownload className="me-2" /> 고화질 이미지 다운로드
                </Button>
                <Button
                  variant="outline-secondary"
                  className="rounded-pill px-3"
                  onClick={() => handleCopyUrl(selectedLook.imageUrl)}
                >
                  {copySuccess ? (
                    <>
                      <FaCheck className="me-1 text-success" /> 복사 완료!
                    </>
                  ) : (
                    <>
                      <FaRegCopy className="me-1" /> 이미지 URL 복사
                    </>
                  )}
                </Button>
                <Button
                  variant="outline-danger"
                  className="rounded-pill px-3"
                  onClick={(e) => handleDeleteLook(e, selectedLook.id)}
                >
                  <FaTimes className="me-1" /> 삭제
                </Button>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* =================================================================== */}
      {/* [오버레이] 생성 진행 중 대기 화면 (최대 10분 소요 대응) */}
      {/* =================================================================== */}
      {isGenerating && (
        <div className="wafit-loading-overlay">
          <div className="wafit-loading-card">
            <div className="wafit-loading-magic-icon">
              <GiSparkles />
            </div>
            <h3 className="wafit-loading-title">AI 피팅이 진행 중이에요</h3>
            <p className="wafit-loading-desc">
              선택하신 옷과 악세사리를 모델에 자연스럽게 입히고 있어요.
              <br />
              고해상도 합성을 위해 최대 2~5분이 소요될 수 있습니다.
            </p>

            <div className="wafit-timer-badge">
              <Spinner size="sm" animation="border" variant="danger" />
              <span>진행 시간: {formatTime(elapsedSeconds)}</span>
            </div>

            <div className="wafit-tip-box">
              <AnimatePresence mode="wait">
                <motion.div
                  key={tipIndex}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.3 }}
                >
                  {LOADING_TIPS[tipIndex]}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
